import { getEnvironmentApiUrls } from '@dvag/dfs-constant-config/api-urls';
import { getContext } from '@dvag/dfs-orchestrator-client';
import {
  advisorHasEnteredHousehold,
  consentHasBeenUpdated,
} from '@dvag/dfs-orchestrator-client/messengers';
import { PersonDto } from 'graphql/person/generates';
import { BasicDataPayload } from 'type/basicData';
import { HouseholdLastAccessedResponse } from 'type/household';
import {
  CommunicationChannelSectionDataType,
  ConsentPayload,
  GetPersonHouseholdListResponse,
  PersonSearchResponseItem,
  UpdateLegalGuardianPayload,
} from 'type/person';
import { TransformParameters } from 'type/taxData';
import { versions } from './versions';
import { getApiClient } from './api-client';

type CommunicationChannelPayload = CommunicationChannelSectionDataType | TransformParameters;

export const personApi = () => {
  const baseURL = `${getEnvironmentApiUrls().person}/${versions.v3}`;

  const api = getApiClient(baseURL);

  const updatePerson = async (personId: string, payload: BasicDataPayload) => {
    const interceptorNr = await api.addResponseInterceptor((value) => {
      const advisorId = getContext()?.user.id;
      consentHasBeenUpdated(advisorId, payload.householdId as string, personId);
      return value;
    });
    const response = await api.put<PersonDto, BasicDataPayload>(`/person/${personId}`, payload);
    await api.removeResponseInterceptor(interceptorNr);
    return response;
  };

  const updateLegalGuardian = async (personId: string, payload: UpdateLegalGuardianPayload) =>
    api.put<never, UpdateLegalGuardianPayload>(`/person/${personId}/legal-guardian`, payload);

  const updateLastAccessedHouseholdList = async (householdId: string) => {
    const interceptorNr = await api.addResponseInterceptor((value) => {
      const advisorId = getContext()?.user.id;
      advisorHasEnteredHousehold(advisorId, householdId);
      return value;
    });
    const response = await api.put<never, never[]>(`/household/${householdId}/last-accessed`, []);
    await api.removeResponseInterceptor(interceptorNr);
    return response;
  };

  const getLastAccessed = async () =>
    api.get<HouseholdLastAccessedResponse[]>('/household/last-accessed');

  const getSearchPerson = async (queryParam: string, options?: { signal?: AbortSignal }) => {
    const params = new URLSearchParams();
    params.append('query', queryParam);

    return api.get<PersonSearchResponseItem[]>(`/person/search?${params}`, options);
  };

  const getPersonHouseholdList = async (personId: string | undefined) =>
    api.get<GetPersonHouseholdListResponse[]>(`/person/${personId}/household`);

  const createCommunicationChannel = async (
    payload: CommunicationChannelPayload,
    personId: string | undefined,
  ) =>
    api.post<
      { context: string; id: string; type: string; value: string },
      CommunicationChannelPayload
    >(`/person/${personId}/communication-channel`, payload);

  const updateCommunicationChannel = async (
    id: string,
    payload: CommunicationChannelPayload,
    personId: string | undefined,
  ) =>
    api.put<
      { context: string; id: string; prefix: string; type: string; value: string },
      CommunicationChannelPayload
    >(`/person/${personId}/communication-channel/${id}`, payload);

  const createConsent = async (personId: string, payload: ConsentPayload) =>
    api.post<
      { email: string; link: string; personId: string; status: string; type: string },
      ConsentPayload
    >(`/person/${personId}/consent`, payload);

  return {
    updatePerson,
    updateLegalGuardian,
    updateLastAccessedHouseholdList,
    getLastAccessed,
    getSearchPerson,
    createCommunicationChannel,
    updateCommunicationChannel,
    createConsent,
    getPersonHouseholdList,
  };
};
